import React, { useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { 
  Activity, 
  Map as MapIcon, 
  Users, 
  User, 
  Settings,
  Group, 
  Footprints,
  MoreHorizontal,
  X
} from 'lucide-react';

const Navigation = ({ orientation = 'vertical' }) => {
  const location = useLocation();
  const currentPath = location.pathname;
  const [showMore, setShowMore] = useState(false);
  
  const navItems = [
    {
      name: 'Dashboard',
      path: '/dashboard',
      icon: Activity,
      primary: true
    },
    {
      name: 'Map',
      path: '/map',
      icon: MapIcon,
      primary: true
    },
    {
      name: 'Challenges',
      path: '/challenges',
      icon: Users,
      primary: false
    },
    {
      name: 'Profile',
      path: '/profile',
      icon: User,
      primary: true
    },
    {
      name: 'Group Dashboard',
      path: '/group-dashboard',
      icon: Group,
      primary: false
    },
    {
      name: 'Hot Stepper Hub',
      path: '/stepper-hub',
      icon: Footprints,
      primary: false
    },
    {
      name: 'Settings',
      path: '/settings',
      icon: Settings,
      primary: false
    }
  ];

  const primaryItems = navItems.filter(item => item.primary);
  const moreItems = navItems.filter(item => !item.primary);

  // For mobile (bottom navigation with more menu)
  if (orientation === 'horizontal') {
    return (
      <div className="relative">
        {/* Overlay */}
        {showMore && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-40"
            onClick={() => setShowMore(false)}
          />
        )}
        
        {/* More menu */}
        {showMore && (
          <div className="absolute bottom-16 right-4 bg-white rounded-lg shadow-lg p-2 z-50 min-w-48">
            <div className="flex justify-between items-center mb-2 px-2">
              <span className="font-medium text-gray-700">More</span>
              <button onClick={() => setShowMore(false)}>
                <X size={16} className="text-gray-500" />
              </button>
            </div>
            {moreItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className="flex items-center w-full px-3 py-2 text-left hover:bg-gray-100 rounded"
                onClick={() => setShowMore(false)}
              >
                <item.icon size={18} className="mr-3 text-gray-600" />
                <span className="text-gray-700">{item.name}</span>
              </Link>
            ))}
          </div>
        )}
        
        {/* Bottom navigation */}
        <nav className="flex justify-between items-center px-4 py-2 bg-white border-t">
          {primaryItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className="flex flex-col items-center min-w-0"
            >
              <div className={`p-2 ${
                currentPath === item.path ? 'text-primary' : 'text-gray-500'
              }`}>
                <item.icon size={20} />
              </div>
              <span className={`text-xs truncate ${
                currentPath === item.path ? 'text-primary font-medium' : 'text-gray-500'
              }`}>
                {item.name}
              </span>
            </Link>
          ))}
          <button
            onClick={() => setShowMore(!showMore)}
            className="flex flex-col items-center"
          >
            <div className="p-2 text-gray-500">
              <MoreHorizontal size={20} />
            </div>
            <span className="text-xs text-gray-500">More</span>
          </button>
        </nav>
      </div>
    );
  }
  
  // For desktop (sidebar navigation) - unchanged
  return (
    <nav className="flex-1">
      <div className="p-4 border-b border-gray-200">
        <h1 className="text-2xl font-bold text-primary">Stepping4Life</h1>
      </div>
      <ul className="space-y-1 py-4">
        {navItems.map((item) => (
          <li key={item.path}>
            <Link
              to={item.path}
              className={`flex items-center px-6 py-3 ${
                currentPath === item.path
                  ? 'bg-primary bg-opacity-10 text-primary border-l-4 border-primary'
                  : 'text-gray-500 hover:bg-gray-100'
              }`}
            >
              <item.icon size={18} className="mr-3" />
              <span>{item.name}</span>
            </Link>
          </li>
        ))}
      </ul>
      
      {/* Premium feature section */}
      <div className="mt-auto px-4 py-4">
        <div className="bg-secondary bg-opacity-10 rounded-lg p-4">
          <div className="flex items-center">
            <MapIcon size={18} className="text-secondary mr-2" />
            <h4 className="font-medium text-secondary">Premium Features</h4>
          </div>
          <p className="text-sm text-gray-600 mt-2">
            Unlock advanced analytics and personalized coaching.
          </p>
          <button className="mt-3 text-sm bg-secondary text-white py-1 px-3 rounded-md w-full">
            Upgrade Now
          </button>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;